import type { UsageRow } from "@/api/log.service";

// O endpoint já entrega os dados agregados por cliente × usuário × módulo ×
// dia; aqui só se reorganiza essa lista plana na árvore de cada visão
// (cliente → usuário → módulo, ou cliente → módulo → usuário).

export interface Totais {
  acessos: number;
  eventos: number;
  erros: number;
  dias: string[]; // dias distintos com atividade, em ordem crescente
  ultimoAcesso: string;
}

export interface No extends Totais {
  id: string;
  nome: string;
  filhos: No[];
}

export type Dimensao = "cliente" | "usuario" | "modulo";

const CHAVE: Record<Dimensao, (l: UsageRow) => string> = {
  cliente: (l) => l.client_id,
  usuario: (l) => l.user_id,
  modulo: (l) => l.module_id,
};

export function totalizar(linhas: UsageRow[]): Totais {
  const dias = new Set<string>();
  let acessos = 0;
  let eventos = 0;
  let erros = 0;
  let ultimoAcesso = "";
  for (const l of linhas) {
    acessos += l.acessos;
    eventos += l.eventos;
    erros += l.erros;
    dias.add(l.dia);
    if (l.ultimo_acesso > ultimoAcesso) ultimoAcesso = l.ultimo_acesso;
  }
  return { acessos, eventos, erros, dias: [...dias].sort(), ultimoAcesso };
}

// Agrupa as linhas pelas dimensões na ordem dada; cada nível vem ordenado por
// dias ativos, depois por volume de eventos, depois por nome. nomeModulo traduz module_id para o rótulo do
// manifest (cai no próprio id quando o módulo não é conhecido).
export function arvore(
  linhas: UsageRow[],
  niveis: Dimensao[],
  nomeModulo: (id: string) => string = (id) => id,
): No[] {
  const [nivel, ...resto] = niveis;
  if (!nivel) return [];
  const grupos = new Map<string, UsageRow[]>();
  for (const l of linhas) {
    const k = CHAVE[nivel](l);
    const g = grupos.get(k);
    if (g) g.push(l);
    else grupos.set(k, [l]);
  }

  const nos = [...grupos].map(([id, ls]): No => {
    const primeira = ls[0]!; // todo grupo tem ao menos uma linha
    const nome =
      nivel === "cliente" ? primeira.client_name : nivel === "usuario" ? primeira.user_name : nomeModulo(id);
    return { id, nome, ...totalizar(ls), filhos: arvore(ls, resto, nomeModulo) };
  });

  return nos.sort(
    (a, b) =>
      b.dias.length - a.dias.length ||
      b.acessos + b.eventos - (a.acessos + a.eventos) ||
      a.nome.localeCompare(b.nome),
  );
}

export interface Resumo {
  clientes: number;
  usuarios: number;
  modulos: number;
  acessos: number;
  dias: number;
}

export function resumo(linhas: UsageRow[]): Resumo {
  return {
    clientes: new Set(linhas.map((l) => l.client_id)).size,
    usuarios: new Set(linhas.map((l) => l.user_id)).size,
    modulos: new Set(linhas.map((l) => l.module_id)).size,
    acessos: linhas.reduce((s, l) => s + l.acessos, 0),
    dias: new Set(linhas.map((l) => l.dia)).size,
  };
}
